import DOMPurify from 'dompurify';
import { Send, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { DialogFooter } from '../../ui/dialog';

const AUDIENCE_DESCRIPTIONS = {
  all: 'Every registered user and subscriber',
  premium: 'Active premium members only',
  purchasers: 'Users who have made purchases',
  engaged: 'Users who liked, commented, or pre-saved',
  inactive: 'Registered but never purchased',
  subscribers: 'Opted-in for new release alerts',
};

export function CampaignPreview({ previewData, sending, onSend, onClose }) {
  if (!previewData) return null;
  
  return (
    <>
      <div className="space-y-4 py-4">
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="font-semibold mb-2">{previewData.campaign.name}</h4>
          <p className="text-sm text-gray-400">Subject: {previewData.campaign.subject}</p>
        </div>

        <div className="bg-white/5 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">Target Audience</span>
            <span className="text-cyan-400 font-bold">{previewData.recipient_count} recipients</span>
          </div>
          <p className="text-sm text-gray-400">
            {AUDIENCE_DESCRIPTIONS[previewData.audience_type]}
          </p>
          {previewData.sample_recipients?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-500 mb-2">Sample recipients:</p>
              <div className="flex flex-wrap gap-2">
                {previewData.sample_recipients.map((r) => (
                  <span key={r.email || r.id} className="text-xs bg-white/10 px-2 py-1 rounded">
                    {r.email}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="font-medium mb-2">Email Preview</h4>
          <div 
            className="bg-black/50 rounded p-4 text-sm"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.campaign.content) }}
            data-testid="campaign-preview-content"
          />
        </div>

        {previewData.campaign.status === 'sent' && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <h4 className="font-medium text-green-400 mb-2">Campaign Sent</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Delivered:</span>
                <span className="ml-2 text-green-400">{previewData.campaign.stats?.sent || 0}</span>
              </div>
              <div>
                <span className="text-gray-400">Failed:</span>
                <span className="ml-2 text-red-400">{previewData.campaign.stats?.failed || 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        {previewData?.campaign.status === 'draft' && (
          <Button 
            onClick={() => onSend(previewData.campaign.id)}
            disabled={sending}
            className="bg-green-500 hover:bg-green-600"
            data-testid="send-campaign-btn"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Now ({previewData.recipient_count} recipients)
              </>
            )}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
